import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { APIConfig } from 'src/app/api';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { zendeskLog, ZendeskLogLevel } from './zendesk-logger';

/**
 * Interface para token JWT do Messenger em cache
 */
interface CachedToken {
  token: string;
  expiry: number;
}

/**
 * Request para geração de token Messenger
 */
interface MessengerTokenRequest {
  userId: string;
  visitorId: string;
}

@Injectable({
  providedIn: 'root',
})
export class ZendeskService {
  private readonly MESSENGER_TOKEN_ENDPOINT = '/v1/zendesk/gera-token-messenger';

  // Cache do Messenger — apenas em memória.
  // Evita rebuscar o JWT no backend a cada re-inicialização do widget dentro
  // da MESMA sessão (ex.: o componente reavaliar o login). Não persistimos em
  // localStorage de propósito: um JWT longevo em localStorage fica exposto a
  // XSS e pode ser servido já expirado após reload. Como o SDK do Messaging
  // mantém a conversa no servidor, basta um JWT novo por carregamento de página.
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hora
  private messengerTokenCache: Map<string, CachedToken> = new Map();

  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: APIConfig
  ) {}

  /**
   * Obtém um token JWT para autenticação do Zendesk Messenger.
   * Cache apenas em memória, por `userId` + `visitorId`, por até 1 hora (o JWT
   * vale 2 horas). Não persiste em localStorage (ver `messengerTokenCache`).
   *
   * @param authToken Token de autorização do usuário
   * @param userId ID do usuário — usado só como chave do cache; o backend tira
   *   a identidade do token de autorização e ignora este campo do corpo
   * @param visitorId ID do visitante (Zendesk Visitor API)
   * @returns Observable com o token JWT
   */
  obterTokenMessenger(
    authToken: string,
    userId: string,
    visitorId: string
  ): Observable<string> {
    if (!authToken) {
      return this.handleError(
        'Token de autorização não encontrado.',
        'warn',
        new Error('Token de autorização ausente')
      );
    }

    if (!userId || !visitorId) {
      return this.handleError(
        'userId ou visitorId não informado.',
        'warn',
        new Error('Parâmetros ausentes')
      );
    }

    // 1. Reaproveitar token em cache (memória) se ainda válido.
    // O keyspace é pequeno (visitorId é estável por dispositivo), mas removemos
    // a entrada assim que expira para não reter token velho em memória.
    const cacheKey = `${userId}_${visitorId}`;
    const cached = this.messengerTokenCache.get(cacheKey);

    if (cached) {
      if (Date.now() < cached.expiry) {
        this.log('Token Messenger reaproveitado do cache em memória');
        return of(cached.token);
      }
      this.messengerTokenCache.delete(cacheKey);
    }

    // 2. Buscar novo token do backend
    this.log('Solicitando novo token Messenger do backend...');

    const url = `${this.apiConfig.customerApiUrl}${this.MESSENGER_TOKEN_ENDPOINT}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': authToken,
    });

    const body: MessengerTokenRequest = { userId, visitorId };

    return this.http
      .post<string>(
        url,
        body,
        { headers, responseType: 'text' as 'json' }
      )
      .pipe(
        tap((jwt) => {
          this.messengerTokenCache.set(cacheKey, {
            token: jwt,
            expiry: Date.now() + this.CACHE_DURATION,
          });
          this.log('Token Messenger obtido e cacheado em memória');
        }),
        catchError((error) =>
          this.handleError('Erro ao obter token JWT Messenger.', 'error', error)
        )
      );
  }

  /**
   * Limpa o cache do Messenger (logout, troca de conta, etc.)
   */
  limparCacheMessenger(): void {
    this.messengerTokenCache.clear();
    this.log('Cache Messenger limpo');
  }

  private handleError(message: string, level: ZendeskLogLevel, error: any): Observable<never> {
    const errorMessage = error.message || 'Desconhecido';
    this.log(`${message} Erro: ${errorMessage}`, level);
    // RxJS 6: `throwError` recebe o próprio erro (a forma com factory é do RxJS 7).
    return throwError(new Error(`${message} ${errorMessage}`));
  }

  private log(message: string, level: ZendeskLogLevel = 'log'): void {
    zendeskLog('ZendeskService', message, level);
  }
}
